import fp from 'lodash/fp';
import React, { useMemo, useEffect, useRef, useCallback, useState } from 'react';


export const useMemoNoWarn = useMemo;
export const useEffectNoWarn = useEffect;
export const useCallbackNoWarn = useCallback;

export const useMemoDeps = <TResult>(
    factory: (...args: any[]) => TResult,
    deps: React.DependencyList,
): TResult => useMemoNoWarn(() => factory(...deps), [factory, ...deps]);

export const useUpdateOnlyEffect = (
    effect: React.EffectCallback,
    deps: React.DependencyList,
): void => {
    const isFirstRun = useRef(true);
    useEffectNoWarn(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return undefined;
        }
        return effect();
    }, deps);
};

export const usePrevious = <TValue, TDefault = TValue>(
    value: TValue,
    defaultValue?: TDefault,
): TValue | TDefault => {
    const ref = useRef<TValue | TDefault>(defaultValue);
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};

export const useTimeoutEffect = (callback: () => void, timeout: number) =>
    useEffectNoWarn(() => {
        const timer = setTimeout(callback, timeout);
        return () => clearTimeout(timer);
    }, []);

export const useInputOnChange = (callback: (value: any) => void, needEvent: boolean) =>
    useCallback(e => callback(needEvent ? e : e.target.value), [callback, needEvent]);

export const useStateRef = <TValue>(initialValue: TValue): [
    TValue,
    React.Dispatch<React.SetStateAction<TValue>>,
    React.RefObject<TValue>
] => {
    const [value, setValue] = useState(initialValue);
    const stateRef = useRef<TValue>(value);
    const setValueAndRef = useMemo<React.Dispatch<React.SetStateAction<TValue>>>(
        () => value => {
            if (fp.isFunction(value)) {
                setValue(prevState => {
                    const newValue = value(prevState);
                    stateRef.current = newValue;
                    return newValue;
                });
            }
            stateRef.current = value as TValue;
            setValue(value);
        },
        [],
    );
    return [value, setValueAndRef, stateRef];
};

export const useNoEvent = <TFunc extends (...args: any[]) => any>(callback: TFunc) =>
    useCallback(() => callback(), [callback]);


const DATA_UNKNOWN = Symbol('DATA_UNKNOWN');
export const useDataRequest = <TData>(data: TData, getData: () => void) => {
    const oldData = usePrevious(data, DATA_UNKNOWN);
    useEffect(
        () => {
            if (!data && oldData !== data)
                getData();
        },
        [data, getData, oldData],
    );
};
